import apiClient from './api'

/** Módulos opcionales (espejo de cl.vantak.entitlement.model.ProductModule). */
export type ModuleCode =
    | 'FIELD_APP' | 'DISPATCH' | 'GEOFENCES' | 'PATROL' | 'SCHEDULING' | 'SAFETY'
    | 'NOTIFICATIONS' | 'EVIDENCE' | 'GEOINTEL' | 'LIVE' | 'SIGNAL' | 'EVALUATION' | 'EMERGENCY' | 'CREDENTIALS'

/** Ruta del frontend → módulo que la habilita (el resto es núcleo, siempre visible). */
export const MODULE_BY_ROUTE: Record<string, ModuleCode> = {
    '/admin/enrollment': 'FIELD_APP',
    '/admin/devices': 'FIELD_APP',
    '/dispatch': 'DISPATCH',
    '/geofences': 'GEOFENCES',
    '/patrol': 'PATROL',
    '/scheduling': 'SCHEDULING',
    '/safety': 'SAFETY',
    '/notifications': 'NOTIFICATIONS',
    '/evidence': 'EVIDENCE',
    '/live': 'LIVE',
    '/signal': 'SIGNAL',
    '/evaluation': 'EVALUATION',
    '/emergencies': 'EMERGENCY',
    '/credentials': 'CREDENTIALS',
}

/** Módulo que exige una ruta (o una subruta, p. ej. /dispatch/12), o null si es del núcleo. */
export function moduleForPath(path: string): ModuleCode | null {
    const hit = Object.keys(MODULE_BY_ROUTE)
        .filter((r) => path === r || path.startsWith(`${r}/`))
        .sort((a, b) => b.length - a.length)[0]
    return hit ? MODULE_BY_ROUTE[hit] : null
}

export interface PlanModuleInfo { code: ModuleCode; label: string; description: string }

export interface Plan {
    id: number
    code: string
    name: string
    description: string | null
    priceClp: number
    maxAgencies: number
    maxUsers: number
    maxOperationsPerMonth: number
    active: boolean
    modules: ModuleCode[]
    tenantCount: number
}

export interface PlanInput {
    code?: string
    name?: string
    description?: string
    priceClp?: number
    maxAgencies?: number
    maxUsers?: number
    maxOperationsPerMonth?: number
    active?: boolean
    modules?: ModuleCode[]
}

export interface PlanImpact {
    addedModules: ModuleCode[]
    removedModules: ModuleCode[]
    affectedTenants: { slug: string; name: string }[]
}

export interface TenantModuleStatus {
    module: ModuleCode
    inPlan: boolean
    override: 'GRANT' | 'REVOKE' | null
    overrideExpiresAt: string | null
    overrideNote: string | null
    effective: boolean
}

export const modulesService = {
    /** Módulos de la organización del usuario (app de clientes). */
    async myModules(): Promise<ModuleCode[]> {
        return (await apiClient.get<{ modules: ModuleCode[] }>('/api/v1/modules')).data.modules
    },

    // ── Back office de plataforma ──
    async catalog(): Promise<PlanModuleInfo[]> {
        return (await apiClient.get('/api/v1/platform/modules')).data
    },
    async plans(): Promise<Plan[]> {
        return (await apiClient.get('/api/v1/platform/plans')).data
    },
    async createPlan(input: PlanInput): Promise<void> {
        await apiClient.post('/api/v1/platform/plans', input)
    },
    async updatePlan(id: number, input: PlanInput, opts: { dryRun: boolean; keepForExisting: boolean }): Promise<PlanImpact> {
        return (await apiClient.put(`/api/v1/platform/plans/${id}`, input, { params: opts })).data
    },
    async tenantModules(slug: string): Promise<TenantModuleStatus[]> {
        return (await apiClient.get(`/api/v1/platform/tenants/${slug}/modules`)).data
    },
    async setTenantModule(slug: string, module: ModuleCode,
                          body: { mode: 'GRANT' | 'REVOKE' | 'NONE'; expiresAt?: string; note?: string }): Promise<TenantModuleStatus[]> {
        return (await apiClient.put(`/api/v1/platform/tenants/${slug}/modules/${module}`, body)).data
    },
}
